"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, FileCheck2, CircleQuestionMark as HelpCircle, Loader as Loader2, Mail, Phone, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const serviceBenefits = [{
  title: "IQ / OQ / PQ Qualification",
  description: "Full regulatory validation protocols customized for GMP/GLP laboratories.",
  icon: FileCheck2
}, {
  title: "Rapid Technical Response",
  description: "Dedicated field engineering and technical support across Ireland and the UK.",
  icon: Clock
}, {
  title: "Full Lifecycle Maintenance",
  description: "Preventative servicing, calibration standards, and scheduled software updates.",
  icon: ShieldCheck
}];
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-border bg-secondary/40 p-6 text-center sm:p-8">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">
          Inquiry Received
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage || "Thank you. Our analytical application specialists will review your requirements and respond within one business day."}
        </p>
        <div className="mt-6">
          <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
            Submit Another Inquiry
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">
                Company Name <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">
                Phone Number <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your instrumentation, testing protocols, or service timeline..." className="min-h-28 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full font-medium">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Submitting Inquiry...
          </> : "Request Lifecycle Consultation"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Your data is processed securely to fulfill your service inquiry. We respect laboratory confidentiality.
      </p>
    </Form>;
}
export default function LifecycleInquiry() {
  const shouldReduceMotion = useReducedMotion();
  const containerMotion = {
    initial: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="lifecycleinquirycta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <motion.div {...containerMotion} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          {/* Left Column: Context, Benefits & Direct Contacts */}
          <div className="lg:col-span-6 flex flex-col justify-between h-full space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                <Sparkles className="size-3.5 mr-1" />
                Dedicated Lifecycle Support
              </Badge>

              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
                Equip your laboratory with reliable lifecycle validation.
              </h2>

              <p className="text-base text-muted-foreground sm:text-lg max-w-xl">
                Antech Solutions provides application-led analytical instrumentation, protocol qualification, and ongoing preventive maintenance for regulated laboratories across Ireland and the UK.
              </p>
            </div>

            {/* Service Highlights List */}
            <div className="space-y-4 pt-2">
              {serviceBenefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return <div key={benefit.title} data-index={index} className="flex items-start gap-4 rounded-lg border border-border bg-secondary/30 p-4 transition-colors">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-background text-primary border border-border shadow-2xs">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">
                        {benefit.title}
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Direct Contact Support Links */}
            <div className="rounded-xl border border-border bg-card p-5 shadow-2xs">
              <h4 className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                Immediate Assistance
              </h4>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm font-medium">
                <a href="mailto:info@antech.ie" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors">
                  <Mail className="size-4 text-primary" />
                  <span>info@antech.ie</span>
                </a>
                <span className="text-border hidden sm:inline">|</span>
                <a href="tel:+35312345678" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors">
                  <Phone className="size-4 text-primary" />
                  <span>+353 (0)1 234 5678</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Inquiry Form Card */}
          <div className="lg:col-span-6">
            <Card className="border-border bg-card shadow-md">
              <CardHeader className="space-y-1.5 pb-4">
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Fill out the form below to connect directly with our technical qualification team.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="contact_form" sectionName="LifecycleInquiryCta" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Request Lifecycle Consultation" successMessage="Thank you for your inquiry. An Antech technical specialist will contact you shortly.">
                  <InquiryFormFields />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}